import React, { useCallback, useRef } from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import {
  Genre,
  Momentum,
  Peakairplay,
  Region,
  Rotation,
} from '../../assets/customIcons';
import {
  InsightsContainerDesktop,
  InsightsCardDesktop,
  InsightsWrapperDesktop,
  CardType,
  CardTypeWrapper,
  CardStatus,
} from './SmartInsights.styles';
import { getMomentumCardContent } from './SmartInsightCardsContent/MomentumSmartInsight';
import { getPeakAirplayCardContent } from './SmartInsightCardsContent/PeakAirplaySmartInsights';
import { getRotationCardContent } from './SmartInsightCardsContent/RotationCardContent';
import { getRegionCardContent } from './SmartInsightCardsContent/RegionCardContent';
import { getGenreCardContent } from './SmartInsightCardsContent/GenreCardContent';
import {
  hasEveningInsight,
  hasGenresInsight,
  hasMomentumInsight,
  hasMorningInsight,
  hasNightInsight,
  hasRegionsInsight,
  hasRotationInsight,
} from './SmartInsightsCardContentHelper';

const EDGE_BAND = 28; // px from left/right that triggers the snap
const COOLDOWN_MS = 400; // avoid retrigger spam while animating
const STEP_BY_CARDS = true; // If true, each step moves by N cards, if false, each step moves by one viewport
const CARDS_PER_STEP = 1;

const SmartInsightsDesktop: React.FC = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cooldownRef = useRef<number>(0);
  const { songInsights } = useSelector((state: RootState) => state.dashboard);

  // Handling desktop pointer detection
  const isDesktopPointer = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Clamping function
  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(v, max));

  // Handling cards
  const getCardDelta = (el: HTMLDivElement) => {
    const children = Array.from(el.children) as HTMLElement[];
    if (children.length < 2) return 0;
    let delta = 0;
    // distance between consecutive items' left edges
    for (let i = 0; i < children.length - 1; i += 1) {
      const r1 = children[i].getBoundingClientRect();
      const r2 = children[i + 1].getBoundingClientRect();
      delta = Math.abs(r2.left - r1.left);
      if (delta > 0) break;
    }
    return delta;
  };

  // Handling step target
  const getStepTarget = (el: HTMLDivElement, dir: -1 | 1) => {
    const maxLeft = el.scrollWidth - el.clientWidth;
    const stepDistance = STEP_BY_CARDS
      ? Math.max(1, Math.round(getCardDelta(el) * CARDS_PER_STEP))
      : el.clientWidth;

    const target = clamp(el.scrollLeft + dir * stepDistance, 0, maxLeft);
    return target;
  };

  // Edge hover auto-scroll
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;

    // desktop
    if (!isDesktopPointer()) return;

    // only when there's something to scroll
    if (el.scrollWidth <= el.clientWidth + 1) return;

    // throttle while animating
    if (Date.now() < cooldownRef.current) return;

    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const atStart = el.scrollLeft <= 2;
    const atEnd =
      Math.abs(el.scrollLeft - (el.scrollWidth - el.clientWidth)) <= 2;

    if (x <= EDGE_BAND && !atStart) {
      const target = getStepTarget(el, -1);
      if (Math.abs(target - el.scrollLeft) > 1) {
        el.scrollTo({ left: target, behavior: 'smooth' });
        cooldownRef.current = Date.now() + COOLDOWN_MS;
      }
    } else if (x >= rect.width - EDGE_BAND && !atEnd) {
      const target = getStepTarget(el, 1);
      if (Math.abs(target - el.scrollLeft) > 1) {
        el.scrollTo({ left: target, behavior: 'smooth' });
        cooldownRef.current = Date.now() + COOLDOWN_MS;
      }
    }
  }, []);

  // Handling clear down
  const clearCooldown = () => {
    cooldownRef.current = 0;
  };

  return (
    <>
      {/* Smart Insights — desktop (display order: Momentum, Peak Airplay, Rotation, Region, Genre) */}
      <InsightsWrapperDesktop
        ref={scrollerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={clearCooldown}
      >
        {/* ---- 1. Momentum ---- */}
        {hasMomentumInsight(songInsights?.songInsightsData) && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop className="momentum-card">
              <CardTypeWrapper>
                <Momentum />
                <CardType>
                  <Trans i18nKey="MOMENTUM_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <span className="default-content">
                  {getMomentumCardContent(songInsights)}
                </span>
                <span className="hover-content">
                  {getMomentumCardContent(songInsights, true)}
                </span>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 2. Peak Airplay (Morning) ---- */}
        {hasMorningInsight(songInsights?.songInsightsData) && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop className="peakairplay-card">
              <CardTypeWrapper>
                <Peakairplay />
                <CardType>
                  <Trans i18nKey="PEAK_AIRPLAY_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <span className="default-content">
                  {getPeakAirplayCardContent(songInsights, 'MORNING')}
                </span>
                <span className="hover-content">
                  {getPeakAirplayCardContent(songInsights, 'MORNING', true)}
                </span>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 2. Peak Airplay (Evening) ---- */}
        {hasEveningInsight(songInsights?.songInsightsData) && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop className="peakairplay-card">
              <CardTypeWrapper>
                <Peakairplay />
                <CardType>
                  <Trans i18nKey="PEAK_AIRPLAY_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <span className="default-content">
                  {getPeakAirplayCardContent(songInsights, 'EVENING')}
                </span>
                <span className="hover-content">
                  {getPeakAirplayCardContent(songInsights, 'EVENING', true)}
                </span>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 2. Peak Airplay (Night) ---- */}
        {hasNightInsight(songInsights?.songInsightsData) && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop className="peakairplay-card">
              <CardTypeWrapper>
                <Peakairplay />
                <CardType>
                  <Trans i18nKey="PEAK_AIRPLAY_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <span className="default-content">
                  {getPeakAirplayCardContent(songInsights, 'NIGHT')}
                </span>
                <span className="hover-content">
                  {getPeakAirplayCardContent(songInsights, 'NIGHT', true)}
                </span>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 3. Rotation ---- */}
        {hasRotationInsight(songInsights?.songInsightsData) && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop className="rotation-card">
              <CardTypeWrapper>
                <Rotation />
                <CardType>
                  <Trans i18nKey="ROTATION_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <span className="default-content">
                  {getRotationCardContent(songInsights)}
                </span>
                <span className="hover-content">
                  {getRotationCardContent(songInsights, true)}
                </span>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 4. Region ---- */}
        {hasRegionsInsight(songInsights?.songInsightsData) && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop className="region-card">
              <CardTypeWrapper>
                <Region />
                <CardType>
                  <Trans i18nKey="REGION_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <span className="default-content">
                  {getRegionCardContent(songInsights)}
                </span>
                <span className="hover-content">
                  {getRegionCardContent(songInsights, true)}
                </span>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 5. Genre ---- */}
        {hasGenresInsight(songInsights?.songInsightsData) && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop className="genre-card">
              <CardTypeWrapper>
                <Genre />
                <CardType>
                  <Trans i18nKey="GENRE_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <span className="default-content">
                  {getGenreCardContent(songInsights)}
                </span>
                <span className="hover-content">
                  {getGenreCardContent(songInsights, true)}
                </span>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}
      </InsightsWrapperDesktop>
    </>
  );
};

export default SmartInsightsDesktop;
